import {
    pushElevarAddToCart,
    pushElevarRemoveFromCart,
    pushElevarLogin,
    pushElevarSelectItem,
    pushElevarSignUp,
    pushElevarUserData,
    pushElevarViewItem,
    pushElevarViewItemList,
    pushElevarViewSearchResults,
    pushElevarViewCart,
    pushElevarPurchase,
    pushElevarSubscribe,
} from './events.ts';
import {
    type EcomProduct,
    type ShippingAddress,
    type UserAttributes,
    type TrackingObject,
} from '../types.ts';
import {
    eventNameConstants,
    stepInteractionConstants,
    trackingConstants,
    triggerConstants,
} from '../constants.ts';
import { omit } from '../utils.ts';
import { compact, resolveProductCategory, toStr } from './helpers.ts';
import { setElevarUserProperties, getElevarCurrencyCode } from './userProperties.ts';
import {
    ELEVAR_LIST_SEARCH_RESULTS,
    ELEVAR_LIST_IMAGE_SEARCH,
    ELEVAR_LIST_CART,
    ELEVAR_LIST_SAVE_FOR_LATER,
    type ElevarPurchaseActionField,
} from './types.ts';

const { EVENT_SIGN_UP, EVENT_LOGIN, EVENT_CART } = eventNameConstants;
const { STEP_INTERACTION_COMPLETE, STEP_INTERACTION_NEW_SOCIAL_ACCOUNT_CONNECTED } =
    stepInteractionConstants;
const { STEP_INTERACTION_REMOVE_CLICK } = stepInteractionConstants;

const SEARCH_LIST_TOKENS = [ELEVAR_LIST_SEARCH_RESULTS, ELEVAR_LIST_IMAGE_SEARCH];

const isSearchList = (list: string): boolean =>
    SEARCH_LIST_TOKENS.some(token => list.includes(token));

const firstList = (
    products?: ReadonlyArray<EcomProduct> | null,
    fallback?: string | null
): string | undefined => {
    const fromProducts = products?.find(p => !!p?.list)?.list;
    return fromProducts ?? fallback ?? undefined;
};

const getList = (products?: ReadonlyArray<EcomProduct> | null): string => {
    const product = products?.find(p => !!resolveProductCategory(p));
    return resolveProductCategory(product ?? {}) || '';
};

export const bridgeUserAttributesToElevar = (attrs: UserAttributes): void => {
    setElevarUserProperties(attrs);
    pushElevarUserData({ currencyCode: getElevarCurrencyCode() });
};

type GtmEcommerceLike = {
    type?: string;
    products?: ReadonlyArray<EcomProduct> | null;
    cartTotal?: string | number | null;
    currencyCode?: string | null;
    trigger?: string | number | null;
};

export const bridgeEcommerceToElevar = (track: GtmEcommerceLike): void => {
    const { type, products, cartTotal, currencyCode: trackCurrencyCode, trigger } = track;
    if (!type) {
        return;
    }

    if (!products || products.length === 0) {
        return;
    }

    const currencyCode = trackCurrencyCode ?? getElevarCurrencyCode();

    switch (type) {
        case trackingConstants.ECOM_PRODUCT_IMPRESSION: {
            const list = firstList(products);
            const normalizedList = list?.toLowerCase();

            if (normalizedList === ELEVAR_LIST_SAVE_FOR_LATER) {
                return;
            }

            if (normalizedList === ELEVAR_LIST_CART) {
                pushElevarViewCart({
                    products,
                    cartTotal: toStr(cartTotal),
                    currencyCode,
                });
                return;
            }

            if (normalizedList && isSearchList(normalizedList)) {
                pushElevarViewSearchResults({ products, currencyCode });
            } else {
                pushElevarViewItemList({ products, currencyCode });
            }
            return;
        }
        case trackingConstants.ECOM_PRODUCT_CLICK: {
            const list = getList(products);
            pushElevarSelectItem({ products, list, currencyCode });
            return;
        }
        case trackingConstants.ECOM_PRODUCT_DETAIL: {
            const list = getList(products);
            pushElevarViewItem({ products, list, currencyCode });
            return;
        }
        case trackingConstants.ECOM_PRODUCT_CART: {
            // Cart / save-for-later purchase CTAs still send GTM addToCart, but the item is
            // already in cart — do not emit Elevar dl_add_to_cart.
            if (trigger === triggerConstants.TRIGGER_CART) {
                return;
            }
            const list = getList(products);
            pushElevarAddToCart({ products, list, currencyCode });
            return;
        }
        default:
            return;
    }
};

type GtmEventLike = {
    eventName?: string;
    step_interaction_name?: string | number;
    additional?: TrackingObject['additional'];
};

export const bridgeEventToElevar = (track: GtmEventLike): void => {
    const { additional, step_interaction_name, eventName } = track;
    // Registration sets additional.marketingOptIn when the user consents to marketing (checked the
    // opt-in box, or is auto-opted-in when not under GDPR). Login flows only include it when
    // marketingOptIn is true and the user was not already subscribed.
    if (additional?.marketingOptIn === true) {
        pushElevarSubscribe(typeof additional.email === 'string' ? additional.email : undefined);
    }

    if (
        eventName === EVENT_SIGN_UP &&
        (step_interaction_name === STEP_INTERACTION_COMPLETE ||
            step_interaction_name === STEP_INTERACTION_NEW_SOCIAL_ACCOUNT_CONNECTED)
    ) {
        pushElevarSignUp();
        return;
    }
    if (eventName === EVENT_LOGIN) {
        pushElevarLogin();
        return;
    }

    if (eventName === EVENT_CART && step_interaction_name === STEP_INTERACTION_REMOVE_CLICK) {
        if (!additional?.id) {
            return;
        }
        const products: EcomProduct[] = [
            omit(additional, ['marketingOptIn', 'email']) as EcomProduct,
        ];
        const list = getList(products);
        pushElevarRemoveFromCart({
            products,
            list,
            currencyCode: getElevarCurrencyCode(),
        });
    }
};
type GtmPurchaseActionField = {
    id?: string | null;
    orderId?: string | null;
    affiliation?: string;
    revenue?: number | string;
    tax?: number | string;
    shipping?: number | string;
    sub_total?: number | string;
    product_sub_total?: number | string;
    step?: number;
};

type GtmPurchaseLike = {
    products?: ReadonlyArray<EcomProduct> | null;
    actionField?: GtmPurchaseActionField | null;
    currencyCode?: string | null;
    shippingAddress?: ShippingAddress | null;
};

// A purchase can be re-emitted (confirmation page remounts/revisits in the same session), which
// would double-count revenue in Elevar. Track pushed order ids so each purchase fires only once.
const pushedPurchaseIds = new Set<string>();

// ** FOR TESTING ** the set is page-view scoped in the browser, tests need it cleared.
export const resetElevarPurchaseDedupe = (): void => {
    pushedPurchaseIds.clear();
};

const mapPurchaseActionField = (
    actionField: GtmPurchaseActionField,
    id: string
): ElevarPurchaseActionField =>
    compact({
        id,
        revenue: toStr(actionField.revenue),
        tax: toStr(actionField.tax) ?? '0.0',
        shipping: toStr(actionField.shipping) ?? '0.0',
        affiliation: actionField.affiliation,
        sub_total: toStr(actionField.sub_total),
        product_sub_total: toStr(actionField.product_sub_total ?? actionField.sub_total),
        sales_channel: 'website',
    });

export const bridgePurchaseToElevar = (track: GtmPurchaseLike): void => {
    const { products, actionField, currencyCode: trackCurrencyCode, shippingAddress } = track;
    if (!products || products.length === 0 || !actionField) {
        return;
    }

    // GTM keeps actionField.id (bundleId on multi-item). Elevar uses customer-facing orderId.
    const orderId = actionField.orderId || actionField.id;
    const bundleId = actionField.id || actionField.orderId;
    if (!orderId || !bundleId) {
        return;
    }

    if (pushedPurchaseIds.has(bundleId)) {
        return;
    }
    pushedPurchaseIds.add(bundleId);

    const currencyCode = trackCurrencyCode ?? getElevarCurrencyCode();

    pushElevarPurchase({
        products,
        actionField: mapPurchaseActionField(actionField, orderId),
        currencyCode,
        shippingAddress,
    });
};
